import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { IconX } from "../layout/Icons";
import type { SettingsSection } from "../settings/SettingsModal";

/**
 * Replit-style "Run a command" palette (CLUI). Opens with Cmd-K / Ctrl-K
 * or via dispatching `vp:clui:open`. Filters a static command list as
 * the user types; Enter / click runs the selected command.
 *
 * Commands either navigate (setLocation) or dispatch a domain event that
 * a feature host listens for (Settings modal, Notifications panel, etc.).
 */

interface Command {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly icon: string;
  readonly run: (ctx: CommandContext) => void;
}

interface CommandContext {
  readonly setLocation: (path: string) => void;
  readonly close: () => void;
  readonly clearInput: () => void;
}

const COMMANDS: ReadonlyArray<Command> = [
  {
    id: "account",
    title: "account",
    description: "Manage your account",
    icon: "◯",
    run: (c) => {
      window.dispatchEvent(
        new CustomEvent<{ section: SettingsSection }>("vp:settings:open", {
          detail: { section: "profile" },
        }),
      );
      c.close();
    },
  },
  {
    id: "settings",
    title: "settings",
    description: "Open the settings dialog",
    icon: "⚙",
    run: (c) => {
      window.dispatchEvent(new CustomEvent("vp:settings:open"));
      c.close();
    },
  },
  {
    id: "billing",
    title: "billing",
    description: "Manage your plan and billing",
    icon: "▦",
    run: (c) => {
      window.dispatchEvent(
        new CustomEvent<{ section: SettingsSection }>("vp:settings:open", {
          detail: { section: "billing" },
        }),
      );
      c.close();
    },
  },
  {
    id: "promotions",
    title: "promotions",
    description: "Referral link, credits, and partner perks",
    icon: "★",
    run: (c) => {
      window.dispatchEvent(
        new CustomEvent<{ section: SettingsSection }>("vp:settings:open", {
          detail: { section: "promotions" },
        }),
      );
      c.close();
    },
  },
  {
    id: "projects",
    title: "projects",
    description: "Open the projects index",
    icon: "▤",
    run: (c) => {
      c.setLocation("/projects");
      c.close();
    },
  },
  {
    id: "published",
    title: "published",
    description: "Open the published projects index",
    icon: "↗",
    run: (c) => {
      c.setLocation("/published");
      c.close();
    },
  },
  {
    id: "templates",
    title: "templates",
    description: "Browse project templates",
    icon: "◇",
    run: (c) => {
      c.setLocation("/templates");
      c.close();
    },
  },
  {
    id: "home",
    title: "home",
    description: "Go to the dashboard",
    icon: "⌂",
    run: (c) => {
      c.setLocation("/");
      c.close();
    },
  },
  {
    id: "notifications",
    title: "notifications",
    description: "Open your notifications",
    icon: "◐",
    run: (c) => {
      window.dispatchEvent(new CustomEvent("vp:notifications:open"));
      c.close();
    },
  },
  {
    id: "clear",
    title: "clear",
    description: "Clears the screen",
    icon: "⌫",
    run: (c) => {
      c.clearInput();
    },
  },
];

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [, setLocation] = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COMMANDS;
    return COMMANDS.filter(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        c.description.toLowerCase().includes(q) ||
        c.id.toLowerCase().includes(q),
    );
  }, [query]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setSelectedIdx(0);
    // Defer focus to after the modal mounts.
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (selectedIdx >= filtered.length) setSelectedIdx(0);
  }, [filtered.length, selectedIdx]);

  if (!open) return null;

  const ctx: CommandContext = {
    setLocation: (p) => setLocation(p),
    close: onClose,
    clearInput: () => {
      setQuery("");
      inputRef.current?.focus();
    },
  };

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      onClose();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(filtered.length - 1, i + 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const target = filtered[selectedIdx];
      if (target) target.run(ctx);
      return;
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Run a command"
      data-testid="command-palette"
      onKeyDown={onKeyDown}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "10vh",
        zIndex: 70,
        backdropFilter: "blur(2px)",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: "min(620px, 92vw)",
          maxHeight: "70vh",
          background: "var(--bg-panel)",
          border: "1px solid var(--border-default)",
          borderRadius: 12,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 80px rgba(0,0,0,0.55)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 14px",
            borderBottom: "1px solid var(--border-default)",
          }}
        >
          <span style={{ color: "var(--text-tertiary)", fontFamily: "monospace" }}>›</span>
          <input
            ref={inputRef}
            type="text"
            placeholder="Run a command"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIdx(0);
            }}
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              color: "var(--text-primary)",
              fontSize: 14,
            }}
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close command palette"
            style={{
              width: 26,
              height: 26,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "transparent",
              border: "1px solid var(--border-default)",
              borderRadius: 6,
              color: "var(--text-tertiary)",
              cursor: "pointer",
            }}
          >
            <IconX size={10} />
          </button>
        </div>
        <ol
          role="listbox"
          style={{
            margin: 0,
            padding: 4,
            listStyle: "none",
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {filtered.length === 0 ? (
            <li
              style={{
                padding: "16px 12px",
                color: "var(--text-tertiary)",
                fontSize: 13,
                textAlign: "center",
              }}
            >
              No commands match.
            </li>
          ) : (
            filtered.map((c, i) => (
              <li
                key={c.id}
                role="option"
                aria-selected={i === selectedIdx}
                onMouseEnter={() => setSelectedIdx(i)}
                onClick={() => c.run(ctx)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "8px 12px",
                  borderRadius: 8,
                  background: i === selectedIdx ? "var(--bg-active)" : "transparent",
                  cursor: "pointer",
                }}
              >
                <span aria-hidden style={{ width: 16, textAlign: "center", color: "var(--text-tertiary)" }}>
                  {c.icon}
                </span>
                <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>
                  {c.title}
                </span>
                <span style={{ flex: 1, fontSize: 12, color: "var(--text-tertiary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.description}
                </span>
                <span aria-hidden style={{ color: "var(--text-tertiary)", fontSize: 12 }}>›</span>
              </li>
            ))
          )}
        </ol>
        <div
          style={{
            borderTop: "1px solid var(--border-default)",
            padding: "10px 14px",
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "var(--bg-panel-2)",
            fontSize: 12,
            color: "var(--text-secondary)",
          }}
        >
          <span aria-hidden style={{ color: "#34d399" }}>✓</span>
          Your balance is $0.00
        </div>
      </div>
    </div>
  );
}
